import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DecimalPipe } from '@angular/common';
import { AddInlineEditTableComponent } from '../tables/add-inline-edit-table/add-inline-edit-table.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { TableColumnsProp } from '../../models/tables.model';
import {
  apChargeCodeFormConfig,
  apChargesTableConfig,
  apInvoiceNumberAutocompleteKey,
  shipmentApChargesTableConfig,
} from './ap-charges.config';
import { shipmentApChargesTableColumnList } from '../tables/tables.config';
import { Router } from '@angular/router';
import {
  ActionIcons,
  ModalPopupMessages,
  ModeIcons,
  validationErrorMessages,
  selectionTableNames,
  StatusCodes,
  BulkDeleteItemNames,
  SingleDeleteHeaderText,
  popupWidth,
  CrudActions,
  selectedIdForSummaryTable,
} from '../../utility/constants';
import {
  DefaultInfoHeaderIcons,
  DefaultInfoTableHeaderIcons,
} from '../../utility/svg-constants';
import {
  FormConfig,
  IDropdownResponse,
  SvgIcon,
  SvgIconProp,
} from '../../models/dynamic.model';
import { CommonService } from '../../services/common.service';
import { ApChargesFormObject, ApChargesService } from './ap-charges.service';
import { ModalService } from '../../services/modal.service';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { FormArray, FormGroup } from '@angular/forms';
import { SnakbarService } from '../../services/snakbar.service';
import { MessagePopupComponent } from '../message-popup/message-popup.component';
import {
  combineLatest,
  concat,
  distinctUntilChanged,
  filter,
  Observable,
  tap,
  toArray,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { RpeSharedTabBinding } from '../../rpe/rpe-shared-tab.binding';
import { RpeRuntimeBridgeService } from '../../services/rpe-runtime-bridge.service';
import { KeyStrokeDirectivesModule } from '../../directives/keystroke/keystrokes.module';
import { CommonPopupService } from '../../services/common-popup.service';
import {
  apChargeDraftRowHasData,
  chargeGridHasActionableValidationErrors,
  getEditableChargeRowIndexesWithData,
  resolveChargeRowDisabledOnPatch,
  withGridSaveForViewModeOnly,
} from '../charge-grid-save-validation.util';

const SHIPMENT_INFORMATION_ROUTE = 'hawb/hawb-information';

@Component({
  selector: 'app-ap-charges',
  standalone: true,
  imports: [
    AddInlineEditTableComponent,
    SvgIconComponent,
    CommonModule,
    DecimalPipe,
    KeyStrokeDirectivesModule,
  ],
  templateUrl: './ap-charges.component.html',
  styleUrl: './ap-charges.component.scss',
})
export class ApChargesComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild(AddInlineEditTableComponent, { static: false })
  private readonly addInlineEditTblComp!: AddInlineEditTableComponent;

  @Output() apChargeSaved = new EventEmitter<void>();
  @Input() rpeGridBinding: RpeSharedTabBinding | null = null;

  private readonly router = inject(Router);
  // SPEC-APL-001: shipment-only behaviour; this grid is shared with MAWB.
  public readonly isShipmentContext = this.router.url.includes(
    SHIPMENT_INFORMATION_ROUTE
  );
  public tableColumnConfig: TableColumnsProp[] = this.isShipmentContext
    ? shipmentApChargesTableConfig
    : apChargesTableConfig;
  public displayedColumnsOverride: string[] | null = this.isShipmentContext
    ? shipmentApChargesTableColumnList
    : null;
  public invoiceAutocompleteKeys: string[] = this.isShipmentContext
    ? [apInvoiceNumberAutocompleteKey]
    : [];
  public invoiceDropdownData: { [key: string]: IDropdownResponse[] } = this
    .isShipmentContext
    ? { [apInvoiceNumberAutocompleteKey]: [] }
    : {};
  public tableName = selectionTableNames;
  public apChargesFormConfig: FormConfig = apChargeCodeFormConfig;
  public apChargesHeaderIcons!: SvgIconProp[];
  public pageMode!: string;
  public apChargesGridForm!: FormGroup;
  public apChargesGridFormData: any;
  public apChargesSelectedRow: any[] = [];
  public apChargesData: ApChargesFormObject[] = [];
  public totalChargeAmount = 0;
  public showTotalRow = false;

  public apChargesButtonClickHandler: Record<
    string,
    () => void | Promise<void>
  > = {
    [ActionIcons.add]: this.handleAPAddClicked.bind(this),
    [ActionIcons.edit]: this.handleAPEditClicked.bind(this),
    [ActionIcons.delete]: this.handleAPDeleteClicked.bind(this),
    [ActionIcons.close]: this.handleAPCancelClicked.bind(this),
    [ActionIcons.save]: this.handleAPSaveClicked.bind(this),
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly rpeRuntimeBridge = inject(RpeRuntimeBridgeService);
  public isAddClicked = false;
  public isEditClicked = false;
  private hasPatched = false;
  private deletedApCharges: ApChargesFormObject[] = [];
  private isInternalUpdate = false;

  constructor(
    private readonly commonService: CommonService,
    private readonly apChargesService: ApChargesService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly modalService: ModalService,
    private readonly commonPopupService: CommonPopupService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly snakbarService: SnakbarService
  ) {}

  ngOnDestroy(): void {
    this.persistChargeGridStateOnTabLeave();
    this.syncChargeGridValidationBlockedState();
  }

  /** Sync draft rows to the shared service before the dynamic tab component is destroyed. */
  private persistChargeGridStateOnTabLeave(): void {
    if (this.pageMode === ModeIcons.view || !this.apChargesGridForm) {
      return;
    }
    const rows = this.getApChargesFormRows();
    if (!rows?.length) {
      return;
    }
    this.syncFormDataToCollection();
  }

  /** Used by Shipment/MAWB main save and AP tab save when the grid has invalid charge rows. */
  public validateChargeGridForSave(showPopup = true): boolean {
    if (!this.apChargesGridForm) {
      return true;
    }
    if (
      this.pageMode === ModeIcons.view &&
      !this.isAddClicked &&
      !this.isEditClicked
    ) {
      this.apChargesService.setChargeGridValidationBlocked(false);
      return true;
    }
    const hasErrors = this.chargeGridHasActionableValidationErrors();
    this.apChargesService.setChargeGridValidationBlocked(hasErrors);
    if (!hasErrors) {
      return true;
    }
    // Blank rows are ignored at save, so only filled draft rows show inline errors.
    const rows = this.getApChargesFormRows();
    this.getApDraftRowIndexesWithData().forEach(index =>
      rows.at(index).markAllAsTouched()
    );
    this.changeDetectorRef.detectChanges();
    if (showPopup) {
      this.commonPopupService.createErrorPopupData(
        this.modalService,
        validationErrorMessages.apModuleChargeValidation,
        ModalPopupMessages.Validation
      );
    }
    return false;
  }

  private syncChargeGridValidationBlockedState(): void {
    if (
      !this.apChargesGridForm ||
      (this.pageMode === ModeIcons.view &&
        !this.isAddClicked &&
        !this.isEditClicked)
    ) {
      this.apChargesService.setChargeGridValidationBlocked(false);
      return;
    }
    this.apChargesService.setChargeGridValidationBlocked(
      this.chargeGridHasActionableValidationErrors()
    );
  }

  private chargeGridHasActionableValidationErrors(): boolean {
    return chargeGridHasActionableValidationErrors(
      this.getApChargesFormRows(),
      this.pageMode,
      apChargeDraftRowHasData
    );
  }

  private getApDraftRowIndexesWithData(): number[] {
    return getEditableChargeRowIndexesWithData(
      this.getApChargesFormRows(),
      apChargeDraftRowHasData
    );
  }

  ngOnInit(): void {
    this.checkMode();
    this.loadApInvoiceNumberOptions();
    this.rpeRuntimeBridge.contextRefresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyRpeFieldPermissions();
        if (this.pageMode) {
          this.getIconsForApChargesTableMode(this.pageMode);
        }
      });
    combineLatest([
      this.apChargesService.dropdownsLoaded$,
      this.apChargesService.getAPTabDetailsCollection$,
    ])
      .pipe(
        filter(([dropdownsLoaded, data]) => dropdownsLoaded && data !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([dropdownsLoaded, data]) => {
        this.apChargesData = data;
        this.hasPatched = false;
        this.tryPatchApChargesData();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // createComponent runs lifecycle before setInput — first binding assign must apply RPE.
    if (changes['rpeGridBinding']?.currentValue && this.apChargesGridForm) {
      this.applyRpeFieldPermissions();
    }
  }

  ngAfterViewInit(): void {
    this.apChargesGridForm = this.addInlineEditTblComp.inlineCrudTableForm;
    this.tryPatchApChargesData();
    this.applyRpeFieldPermissions();
  }

  /** SPEC-APL-001 AC-APL-003: Invoice Number typeahead source (shipment only). */
  private loadApInvoiceNumberOptions(): void {
    if (!this.isShipmentContext) {
      return;
    }
    this.apChargesService
      .getApInvoiceNumberOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: any) => {
        this.invoiceDropdownData = {
          [apInvoiceNumberAutocompleteKey]:
            response?.statusCode === StatusCodes.Success
              ? (response.data ?? []).filter(
                  (option: IDropdownResponse) => !!option?.name
                )
              : [],
        };
        this.changeDetectorRef.detectChanges();
        this.addInlineEditTblComp?.initializeExternalDropdownsForAllRows();
      });
  }

  private applyRpeFieldPermissions(): void {
    if (!this.rpeGridBinding || !this.apChargesGridForm) {
      return;
    }
    this.rpeGridBinding.applyToForm(this.apChargesGridForm);
  }

  private tryPatchApChargesData(): void {
    if (
      this.apChargesGridForm &&
      !this.hasPatched &&
      this.apChargesData?.length >= 0
    ) {
      this.hasPatched = true;
      this.patchApChargesData();
    }
  }

  public onAPChargesGridFormFieldChange(formValue: any): void {
    this.apChargesGridFormData = formValue;
    // Grid Save (view mode only) is enabled once any draft row has a value; rows are validated on click.
    const hasDraftRowData = this.getApDraftRowIndexesWithData().length > 0;
    if (hasDraftRowData && !this.isViewLikePageMode(this.pageMode)) {
      this.commonService.checkPristineAccordionData(false);
    }
    this.apChargesHeaderIcons = this.apChargesHeaderIcons.map(
      (icon: SvgIconProp) => {
        if (icon.name === ActionIcons.save) {
          icon.isDisabled = !hasDraftRowData;
        }
        return icon;
      }
    );
  }

  public onAPChargesTableSelectionChange(selectedRows: any[]): void {
    // SPEC-APL-001: locked (published AR) rows can never be edited/deleted
    this.apChargesSelectedRow = selectedRows.filter(
      row => row?.isLocked !== true
    );
    this.updateIconsBasedOnSelection(this.apChargesSelectedRow.length > 0);
  }

  public onAPChargesIconClicked(iconClicked: SvgIcon): void {
    if (this.apChargesButtonClickHandler[iconClicked.name]) {
      this.apChargesButtonClickHandler[iconClicked.name]();
    }
  }

  private handleAPAddClicked(): void {
    if (this.isViewModeChargesMutateBlocked()) {
      return;
    }
    this.appendEmptyApChargeRowFromAddAction();
  }

  /** FR-041 — View-mode AP mutate only when flag off / super admin. */
  private isViewModeChargesMutateBlocked(
    mode: string = this.pageMode
  ): boolean {
    return (
      this.isViewLikePageMode(mode) &&
      !this.rpeRuntimeBridge.canMutateInformationTabsInViewMode()
    );
  }

  private isViewLikePageMode(mode: string): boolean {
    const normalized = (mode ?? '').toString().toLowerCase();
    return (
      mode === ModeIcons.view ||
      mode === ModeIcons.smallview ||
      normalized === 'view'
    );
  }

  public onAppendChargeRowFromKeyboard(event: { rowIndex: number }): void {
    if (this.isViewModeChargesMutateBlocked()) {
      return;
    }
    const rowIndex = event.rowIndex;
    const rows = this.getApChargesFormRows();
    const row = rows.at(rowIndex);
    if (!row || row.get('isRowDisable')?.value) {
      return;
    }
    this.isAddClicked = true;
    this.insertApChargeRowAt(0);
    this.focusApChargesRow(0);
  }

  /** Add / Alt+A: insert another empty editable row at the top without clearing existing rows. */
  private appendEmptyApChargeRowFromAddAction(): void {
    this.isAddClicked = true;
    this.getIconsForAddIconClicked();
    const rows = this.getApChargesFormRows();
    if (!rows.length) {
      this.addInlineEditTblComp.addNewRow();
      this.addInlineEditTblComp.getRows().at(0).patchValue({
        isRowDisable: false,
        action: 'create',
        tempId: this.commonService.generateUniqueTempId(),
        invoiceChargesId: 0,
      });
      this.inputRowChanges(0);
      this.addInlineEditTblComp.loadAutocompleteForNewRow(0);
    } else {
      this.insertApChargeRowAt(0);
      this.focusApChargesRow(0);
    }
    this.addInlineEditTblComp.getTableData();
    this.changeDetectorRef.detectChanges();
    this.applyRpeFieldPermissions();
    this.commonService.checkPristineAccordionData(false);
  }

  /** Add / Tab: insert an empty editable row without rebuilding the grid (preserves other rows). */
  private insertApChargeRowAt(insertIndex: number): void {
    this.addInlineEditTblComp.insertNewRowAt(insertIndex);
    this.addInlineEditTblComp.getRows().at(insertIndex).patchValue({
      isRowDisable: false,
      action: 'create',
      tempId: this.commonService.generateUniqueTempId(),
      invoiceChargesId: 0,
    });
    this.inputRowChanges(insertIndex);
    this.addInlineEditTblComp.loadAutocompleteForNewRow(insertIndex);
    this.addInlineEditTblComp.getTableData();
    this.changeDetectorRef.detectChanges();
    this.applyRpeFieldPermissions();
    this.commonService.checkPristineAccordionData(false);
  }

  private focusApChargesRow(rowIndex: number): void {
    const fieldKey =
      this.addInlineEditTblComp.getFirstFocusableChargeColumnKey();
    this.changeDetectorRef.detectChanges();
    requestAnimationFrame(() => {
      this.addInlineEditTblComp.focusField(rowIndex, fieldKey);
    });
  }

  public addNewRow(): void {
    this.isAddClicked = true;
    this.getIconsForAddIconClicked();
    const formArray = this.getApChargesFormRows();
    const existingData = formArray.value;
    formArray.clear();
    this.addInlineEditTblComp.addNewRow();
    this.addInlineEditTblComp.loadAutocompleteForNewRow(0);
    if (existingData && existingData.length > 0) {
      existingData.forEach((data: any, index: number) => {
        this.addInlineEditTblComp.addNewRow();
        const newIndex = index + 1;
        formArray.at(newIndex).patchValue(data);
        formArray.at(newIndex).disable();
        this.addInlineEditTblComp.loadAutocompleteForNewRow(newIndex);
      });
    }
    this.inputRowChanges(0);
    this.addInlineEditTblComp.getTableData();
    this.changeDetectorRef.detectChanges();
    this.applyRpeFieldPermissions();
  }

  private getIconsForAddIconClicked() {
    const apChargesHeaderIcons = structuredClone(DefaultInfoHeaderIcons);
    const actionIconNames = new Set([
      ActionIcons.add,
      ActionIcons.edit,
      ActionIcons.delete,
      ActionIcons.save,
      ActionIcons.close,
    ]);
    this.apChargesHeaderIcons = withGridSaveForViewModeOnly(
      apChargesHeaderIcons
        .filter((val: SvgIconProp) => actionIconNames.has(val.name))
        .map((val: SvgIconProp) => {
          val.isDisabled = this.isChargeGridAddFlowIconDisabled(val.name);
          return val;
        }),
      this.isViewLikePageMode(this.pageMode)
    );
  }

  /** During add flow: keep Add and Close enabled so users can insert multiple rows. */
  private isChargeGridAddFlowIconDisabled(iconName: string): boolean {
    if (iconName === ActionIcons.add || iconName === ActionIcons.close) {
      return false;
    }
    if (iconName === ActionIcons.save) {
      return true;
    }
    return true;
  }

  private handleAPEditClicked(): void {
    if (this.isViewModeChargesMutateBlocked()) {
      return;
    }
    this.isEditClicked = true;
    this.apChargesSelectedRow.forEach((charge: any) => {
      if (charge.isLocked === true) {
        return;
      }
      const param =
        charge.invoiceChargesId === 0
          ? selectedIdForSummaryTable.tempId
          : 'invoiceChargesId';
      this.getApChargesFormRows().controls.forEach((control, index) => {
        if (control.value[param] === charge[param]) {
          this.getApChargesFormRows()
            .at(index)
            .get('isRowDisable')
            ?.setValue(false);
          this.addInlineEditTblComp.loadAutocompleteForNewRow(index);
          this.inputRowChanges(index);
        }
      });
    });
    this.addInlineEditTblComp.inlineTableSelection.clear();
    this.getIconsForApChargesTableMode(ModeIcons.add);
    this.applyRpeFieldPermissions();
  }

  private handleAPDeleteClicked(): void {
    if (this.isViewModeChargesMutateBlocked()) {
      return;
    }
    let message: string;
    let messageType: string;
    if (this.apChargesSelectedRow.length > 1) {
      message = ModalPopupMessages.bulkDeleteMessage(
        this.apChargesSelectedRow.length,
        BulkDeleteItemNames.apCharges
      );
      messageType = ModalPopupMessages.BulkDeleteHeader;
    } else {
      message =
        ModalPopupMessages.singleDeleteMessage(
          SingleDeleteHeaderText.apCharges
        ) + '?';
      messageType = ModalPopupMessages.singleDeleteHeader(
        SingleDeleteHeaderText.apCharges
      );
    }
    const dialogRef = this.modalService.openPopup(
      {
        message: message,
        isMultiple: true,
        isFooterRequired: true,
        isBulk: this.apChargesSelectedRow.length > 1,
      },
      messageType,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.deleteApCharges();
        }
      });
  }

  private deleteApCharges(): void {
    const selectedForDelete = [...this.apChargesSelectedRow];
    selectedForDelete.forEach((charge: any) => {
      const param =
        this.commonService.isValidField(charge.invoiceChargesId) &&
        charge.invoiceChargesId !== 0
          ? selectedIdForSummaryTable.invoiceChargesId
          : selectedIdForSummaryTable.tempId;
      const rows = this.getApChargesFormRows();
      const indexToRemove: number[] = [];
      rows.controls.forEach((control, index) => {
        if (control.value[param] === charge[param]) {
          indexToRemove.push(index);
        }
      });
      indexToRemove.sort((a, b) => b - a);
      indexToRemove.forEach(index => {
        const rowToDelete = rows.at(index).getRawValue();
        if (param === selectedIdForSummaryTable.invoiceChargesId) {
          rowToDelete.action = CrudActions.delete;
          this.deletedApCharges.push(rowToDelete);
        }
        rows.removeAt(index);
      });
      this.addInlineEditTblComp.getTableData();
    });
    if (this.pageMode === ModeIcons.view) {
      this.deleteApChargesInViewMode(selectedForDelete);
    } else {
      this.getIconsForApChargesTableMode(ModeIcons.add);
      this.addInlineEditTblComp.inlineTableSelection.clear();
      this.syncFormDataToCollection();
      this.commonService.checkPristineAccordionData(false);
    }
  }

  private deleteApChargesInViewMode(selectedForDelete: any[]): void {
    const deletedApCharges =
      this.apChargesService.buildPayloadForDeleteAPChargeInViewMode(
        selectedForDelete
      );
    this.apChargesService
      .deleteApCharge(deletedApCharges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode === StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              data.message,
              ModalPopupMessages.success
            );
            this.addInlineEditTblComp.inlineTableSelection.clear();
            this.apChargeSaved.emit();
          } else {
            this.errorHandlerService.handleApiError(data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private handleAPCancelClicked(): void {
    this.isAddClicked = false;
    this.isEditClicked = false;
    this.deletedApCharges = []; // Clear deleted charges on cancel
    this.getApChargesFormRows().at(0).reset();
    this.getApChargesFormRows().at(0).enable();
    if (this.pageMode === ModeIcons.view) {
      this.getIconsForApChargesTableMode(ModeIcons.view);
    } else {
      this.getApChargesFormRows().at(0).enable();
      this.getIconsForApChargesTableMode(ModeIcons.add);
    }
    this.hasPatched = false;
    this.patchApChargesData();
    if (this.pageMode === ModeIcons.view) {
      this.apChargesService.resetUnsavedChanges();
      this.commonService.checkPristineAccordionData(true);
      this.snakbarService.openCustomisedSnackBar(
        ModalPopupMessages.actionCancel,
        ModalPopupMessages.success
      );
    }
  }

  private handleAPSaveClicked(): void {
    if (this.isViewModeChargesMutateBlocked()) {
      return;
    }
    if (this.pageMode !== ModeIcons.view) {
      if (!this.validateChargeGridForSave(true)) {
        return;
      }
      this.isEditClicked ? this.saveAPEditedData() : this.saveAPAddedData();
    } else if (this.pageMode === ModeIcons.view) {
      if (!this.validateChargeGridForSave(true)) {
        return;
      }
      this.saveApDraftRowsInViewMode();
    }
  }

  /**
   * View mode (no Shipment/MAWB Save): persist every draft row with values.
   * One request per invoice; new rows via create, edited rows via update; sent sequentially.
   */
  private saveApDraftRowsInViewMode(): void {
    const rows = this.getApChargesFormRows();
    const draftRows = this.getApDraftRowIndexesWithData().map(index =>
      rows.at(index).getRawValue()
    );
    const isNewRow = (row: any) => !(+(row.invoiceChargesId || 0) > 0);
    const requests: Observable<any>[] = [
      ...this.groupApRowsByInvoice(draftRows.filter(isNewRow)).map(group =>
        this.apChargesService.createApCharge(
          this.apChargesService.buildPayloadForSaveOrUpdateAPChargeInViewMode(
            group,
            true
          )
        )
      ),
      ...this.groupApRowsByInvoice(draftRows.filter(row => !isNewRow(row))).map(
        group =>
          this.apChargesService.updateApCharge(
            this.apChargesService.buildPayloadForSaveOrUpdateAPChargeInViewMode(
              group,
              false
            )
          )
      ),
    ];
    if (!requests.length) {
      return;
    }
    let savedCount = 0;
    concat(...requests)
      .pipe(
        tap(response => {
          if (response?.statusCode === StatusCodes.Success) {
            savedCount++;
          }
        }),
        toArray(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (responses: any[]) => {
          const failed = responses.find(
            response => response?.statusCode !== StatusCodes.Success
          );
          if (failed) {
            // Other error codes already get a red toast from errorHandler().
            if (failed.statusCode === StatusCodes.SuccessfulRequest) {
              this.snakbarService.openCustomisedSnackBar(
                failed.message,
                ModalPopupMessages.error
              );
            }
          } else {
            this.isAddClicked = false;
            this.isEditClicked = false;
            this.snakbarService.openCustomisedSnackBar(
              responses[responses.length - 1]?.message,
              ModalPopupMessages.success
            );
          }
          // Refresh only when something was persisted, so a failed save keeps the user's rows.
          if (savedCount > 0) {
            this.apChargeSaved.emit();
          }
        },
        // Red error toast is shown by errorHandler() in the service call.
        error: () => {
          if (savedCount > 0) {
            this.apChargeSaved.emit();
          }
        },
      });
  }

  private groupApRowsByInvoice(rows: any[]): any[][] {
    const groups = new Map<string, any[]>();
    rows.forEach(row => {
      const key = +(row.apInvoiceId || 0)
        ? `id:${row.apInvoiceId}`
        : `new:${row.invoiceNumber ?? ''}|${row.agent ?? row.agentId ?? ''}`;
      groups.set(key, [...(groups.get(key) ?? []), row]);
    });
    return [...groups.values()];
  }

  private saveAPEditedData() {
    if (!this.apChargesGridForm.valid) {
      this.validateChargeGridForSave(true);
      return;
    }
    if (this.apChargesGridForm.valid) {
      this.getApChargesFormRows().at(0).enable();
      this.getApChargesFormRows().controls.forEach((control, index) => {
        if (index > 0 && control.value.isRowDisable === false) {
          const editedRow = control.getRawValue();
          editedRow.isRowDisable = true;
          control.patchValue(editedRow);
        }
      });
      this.isEditClicked = false;
      this.addInlineEditTblComp.getTableData();
      this.syncFormDataToCollection();
      this.commonService.checkPristineAccordionData(false);
      this.apChargesService.setChargeGridValidationBlocked(false);
    }
  }

  private saveAPAddedData() {
    if (!this.apChargesGridForm.valid) {
      this.validateChargeGridForSave(true);
      return;
    }
    if (this.apChargesGridForm.valid) {
      this.addInlineEditTblComp.addNewRow();
      const newRow = this.getApChargesFormRows().at(0).getRawValue();
      newRow.isRowDisable = true;
      newRow.tempId = this.commonService.generateUniqueTempId();
      this.addInlineEditTblComp.loadAutocompleteForNewRow(
        this.getApChargesFormRows().length - 1
      );
      this.getApChargesFormRows()
        .at(this.getApChargesFormRows().length - 1)
        .patchValue(newRow);
      this.getApChargesFormRows()
        .at(this.getApChargesFormRows().length - 1)
        .disable();
      this.getApChargesFormRows().at(0).reset();
      this.apChargesHeaderIcons = this.apChargesHeaderIcons.map(
        (icon: SvgIconProp) => {
          if (icon.name === ActionIcons.save) {
            icon.isDisabled = true;
          }
          return icon;
        }
      );
      this.addInlineEditTblComp.getTableData();
      this.changeDetectorRef.detectChanges();
      this.syncFormDataToCollection();
      this.commonService.checkPristineAccordionData(false);
      this.apChargesService.setChargeGridValidationBlocked(false);
    }
  }

  private getApChargesFormRows(): FormArray {
    return this.apChargesGridForm.get('rows') as FormArray;
  }

  public syncFormDataToCollection(): void {
    const formRows = this.getApChargesFormRows();
    const allCharges: ApChargesFormObject[] = [];
    const parentDetails = this.apChargesService.getDetailsForAPTab();
    formRows.controls.forEach(control => {
      const rowData = control.getRawValue();
      if (
        this.pageMode !== ModeIcons.view &&
        control.enabled &&
        !apChargeDraftRowHasData(rowData)
      ) {
        return; // Skip blank editable rows
      }
      if (control.enabled) {
        rowData.isRowDisable = false; // Draft row: saved with Shipment/MAWB Save
      }
      const processedRow = this.processChargeData(rowData, parentDetails);
      allCharges.push(processedRow);
    });

    this.isInternalUpdate = true;
    if (allCharges.length > 0) {
      this.apChargesService.updateAPChargesCollection(allCharges);
    }
    if (this.deletedApCharges.length > 0) {
      const processedDeletedCharges = this.deletedApCharges.map(deletedCharge =>
        this.processChargeData({ ...deletedCharge }, parentDetails)
      );
      this.apChargesService.updateDeletedAPCharges(processedDeletedCharges);
    } else {
      this.apChargesService.updateDeletedAPCharges([]);
    }
    this.isInternalUpdate = false;
  }

  private processChargeData(
    chargeData: any,
    parentDetails: any
  ): ApChargesFormObject {
    if (!chargeData.destinationId && parentDetails.destinationId) {
      chargeData.destinationId = parentDetails.destinationId;
    }
    if (!chargeData.weight && parentDetails.weight) {
      chargeData.weight = parentDetails.weight;
    }
    if (!chargeData.shipmentIdentifierId) {
      chargeData.shipmentIdentifierId = 0;
    }
    if (!chargeData.shipmentId && parentDetails.id) {
      chargeData.shipmentId = parentDetails.id;
    }
    if (!chargeData.shipmentIdentifierValue && parentDetails.ext) {
      chargeData.shipmentIdentifierValue = parentDetails.ext;
    }
    if (!chargeData.shipmentIdentifier) {
      chargeData.shipmentIdentifier = parentDetails.isFromHawb
        ? SingleDeleteHeaderText.Shipment
        : SingleDeleteHeaderText.MawbCaps;
    }
    if (!chargeData.action) {
      chargeData.action = 'c';
    }
    chargeData.invoiceChargesId = this.commonService.isValidField(
      chargeData.invoiceChargesId ?? 0
    )
      ? +(chargeData.invoiceChargesId ?? 0)
      : 0;
    return chargeData;
  }

  private patchApChargesData() {
    const formArray = this.getApChargesFormRows();
    formArray.clear();
    if (!this.isInternalUpdate) {
      this.deletedApCharges = this.apChargesService.getDeletedApCharges();
    }
    const transformedData = this.apChargesService.getTransformedData();
    if (this.pageMode !== ModeIcons.view && transformedData.length === 0) {
      this.addInlineEditTblComp.addNewRow();
      this.inputRowChanges(0);
      this.addInlineEditTblComp.getTableData();
      this.changeDetectorRef.detectChanges();
      this.applyRpeFieldPermissions();
      return;
    }
    if (this.pageMode !== ModeIcons.view && transformedData.length > 0) {
      this.addInlineEditTblComp.addNewRow();
      this.addInlineEditTblComp.loadAutocompleteForNewRow(0);
      this.inputRowChanges(0);
    }
    transformedData.forEach((charge, index) => {
      this.addInlineEditTblComp.addNewRow();
      const dataIndex = this.pageMode === ModeIcons.view ? index : index + 1;
      this.addInlineEditTblComp.loadAutocompleteForNewRow(dataIndex);
      // SPEC-APL-001: published-AR rows always stay read-only after re-render
      const isRowDisabled =
        charge.isLocked === true ||
        resolveChargeRowDisabledOnPatch(
          this.pageMode,
          dataIndex,
          charge.isRowDisable
        );
      const transformedCharge = { ...charge, isRowDisable: isRowDisabled };
      formArray.at(dataIndex).patchValue(transformedCharge);
      if (isRowDisabled) {
        formArray.at(dataIndex).disable();
      } else {
        this.inputRowChanges(dataIndex);
      }
    });
    this.addInlineEditTblComp.getTableData();
    this.changeDetectorRef.detectChanges();
    if (
      this.pageMode !== ModeIcons.view &&
      this.apChargesService.getHasUnsavedChanges()
    ) {
      this.commonService.checkPristineAccordionData(false);
    }
    this.restoreChargeGridUiStateAfterPatch();
    this.applyRpeFieldPermissions();
  }

  private restoreChargeGridUiStateAfterPatch(): void {
    if (this.pageMode === ModeIcons.view || !this.apChargesHeaderIcons) {
      return;
    }
    const rows = this.getApChargesFormRows();
    const hasEditableRow = rows.controls.some(
      control => control.get('isRowDisable')?.value === false
    );
    if (!hasEditableRow) {
      return;
    }
    this.isAddClicked = true;
    const firstEditable = rows.controls.find(
      control => control.get('isRowDisable')?.value === false
    );
    if (firstEditable) {
      this.onAPChargesGridFormFieldChange(firstEditable.getRawValue());
    }
  }

  private inputRowChanges(index: number) {
    const formArray = this.getApChargesFormRows();
    formArray.at(index).enable();
    formArray.at(index).get('invoiceDate')?.disable();
    formArray.at(index).get('incomingChargeCurrency')?.disable();
    formArray.at(index).get('agentId')?.disable();
    formArray.at(index).get('conversionRate')?.disable();
    formArray.at(index).get('arInvoiceNumber')?.disable();
  }

  private updateIconsBasedOnSelection(hasSelection: boolean): void {
    const modifiedIcons = this.apChargesHeaderIcons.map((icon: SvgIconProp) => {
      if (icon.name === ActionIcons.delete) {
        icon.isDisabled = !hasSelection;
      } else if (icon.name === ActionIcons.edit) {
        icon.isDisabled = this.apChargesSelectedRow.length !== 1;
      } else if (icon.name === ActionIcons.add) {
        icon.isDisabled = hasSelection;
      } else if (
        icon.name === ActionIcons.save &&
        this.pageMode !== ModeIcons.view
      ) {
        if (hasSelection) {
          icon.isDisabled = true;
        }
      } else if (icon.name === ActionIcons.close) {
        if (hasSelection) {
          icon.isDisabled = true;
        } else {
          const hasEditableRow = this.getApChargesFormRows()?.controls.some(
            control => !control.get('isRowDisable')?.value
          );
          icon.isDisabled =
            this.pageMode === ModeIcons.view ? !hasEditableRow : false;
        }
      }
      return icon;
    });
    this.apChargesHeaderIcons = modifiedIcons;
  }

  private getIconsForApChargesTableMode(mode: string): void {
    const apChargesHeaderIcons = structuredClone(DefaultInfoTableHeaderIcons);
    const filteredIconList: SvgIconProp[] = [];
    const isBulkEditContext = this.isBulkEditContextFromUrl();
    const isBulkEditViewMode = this.commonService.isBulkEditViewMode.value;
    const isBulkEditMode =
      isBulkEditContext &&
      (isBulkEditViewMode ||
        mode === ModeIcons.bulkEdit ||
        this.pageMode === ModeIcons.bulkEdit);

    if (isBulkEditMode) {
      apChargesHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.name !== ActionIcons.view) {
          filteredIconList.push(val);
        }
      });
    } else if (mode === ModeIcons.add || mode === ModeIcons.edit) {
      apChargesHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.name === ActionIcons.view) {
          return;
        }
        if (val.name === ActionIcons.add) {
          val.isDisabled = false;
        } else if (val.name === ActionIcons.save) {
          val.isDisabled = true;
        }
        filteredIconList.push(val);
      });
    } else if (this.isViewLikePageMode(mode)) {
      if (this.isViewModeChargesMutateBlocked(mode)) {
        this.apChargesHeaderIcons = [];
        this.changeDetectorRef.detectChanges();
        return;
      }
      apChargesHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.name === ActionIcons.add) {
          val.isDisabled = false;
        } else if (
          val.name === ActionIcons.edit ||
          val.name === ActionIcons.delete ||
          val.name === ActionIcons.save ||
          val.name === ActionIcons.close
        ) {
          val.isDisabled = true;
        }
        if (val.name !== ActionIcons.view) {
          filteredIconList.push(val);
        }
      });
    }

    this.apChargesHeaderIcons = withGridSaveForViewModeOnly(
      filteredIconList,
      this.isViewLikePageMode(this.pageMode)
    );

    if (isBulkEditMode) {
      this.apChargesHeaderIcons = this.apChargesHeaderIcons.map(
        (icon: SvgIconProp) => {
          icon.isDisabled = true;
          return icon;
        }
      );
      this.changeDetectorRef.detectChanges();
    } else {
      const disabledIcons =
        this.commonService.disableAllIconsInBulkEditViewMode(
          this.pageMode,
          this.apChargesHeaderIcons
        );
      if (disabledIcons) {
        this.apChargesHeaderIcons = disabledIcons;
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  private isBulkEditContextFromUrl(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    return mode === ActionIcons.bulkEdit;
  }

  private checkMode(): void {
    this.commonService.isCheckForMode
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((data: string) => {
        if (data === ModeIcons.view) {
          this.pageMode = ModeIcons.view;
        } else {
          this.pageMode = data === '' ? ModeIcons.add : data;
        }
        this.getIconsForApChargesTableMode(this.pageMode);
        this.applyRpeFieldPermissions();
      });
  }
}
